import React, { useEffect, useState } from "react";
import { FileText, Eye, PenLine, Send, Share2 } from "lucide-react";
import { API_BASE_URL } from "src/config/apiBase";
import { decryptToken } from "../utils/getToken";
import { toast } from "react-toastify";

const STATUS_LABELS = {
  draft: "Draft",
  sent: "Sent",
  viewed: "Viewed",
  signed: "Signed",
  declined: "Declined",
};

const CrmDocumentTracker = ({ leadId, compact = false }) => {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const url = API_BASE_URL;
  const token = decryptToken("accessToken") || "";

  const load = () => {
    if (!leadId) return;
    setLoading(true);
    fetch(`${url}/leads/documents/${leadId}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => (r.ok ? r.json() : null))
      .then((res) => setDocuments(res?.documents || []))
      .finally(() => setLoading(false));
  };

  useEffect(load, [leadId, url, token]);

  const updateStatus = async (documentId, status) => {
    if (!leadId || saving) return;
    setSaving(true);
    try {
      const res = await fetch(`${url}/leads/documents/${leadId}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ documentId, status, autoConvert: status === "signed" }),
      });
      if (!res.ok) throw new Error("Update failed");
      const data = await res.json();
      setDocuments(data.documents || []);
      if (status === "signed") {
        toast.success("Marked signed — lead moved to Converted if applicable");
      } else {
        toast.success(`Document marked ${STATUS_LABELS[status] || status}`);
      }
    } catch {
      toast.error("Could not update document");
    } finally {
      setSaving(false);
    }
  };

  const addQuote = async () => {
    if (!leadId || saving) return;
    setSaving(true);
    try {
      const res = await fetch(`${url}/leads/documents/${leadId}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ type: "quote", title: "Quotation", status: "sent" }),
      });
      if (!res.ok) throw new Error("Create failed");
      const data = await res.json();
      setDocuments(data.documents || []);
      toast.success("Quote logged as sent");
    } catch {
      toast.error("Could not log quote");
    } finally {
      setSaving(false);
    }
  };

  const sendForSignature = async (doc) => {
    if (!leadId || saving) return;
    setSaving(true);
    try {
      const res = await fetch(`${url}/leads/esign-request/${leadId}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          documentId: doc?.id,
          title: doc?.title || "Quotation",
          amount: doc?.amount,
        }),
      });
      if (!res.ok) throw new Error("Failed");
      const data = await res.json();
      if (data.signUrl) {
        navigator.clipboard?.writeText(data.signUrl);
        toast.success("Signing link copied to clipboard");
      }
      load();
    } catch {
      toast.error("Could not create signing link");
    } finally {
      setSaving(false);
    }
  };

  if (!leadId) return null;
  if (loading) return <p className="crm-insights__muted">Loading documents…</p>;

  return (
    <div className={`crm-doc-tracker${compact ? " crm-doc-tracker--compact" : ""}`}>
      <div className="crm-doc-tracker__head">
        <FileText size={16} />
        <strong>Documents</strong>
        <button type="button" className="crm-doc-tracker__add" onClick={addQuote} disabled={saving}>
          <Send size={14} /> Log quote sent
        </button>
        {documents[0] && documents[0].status !== "signed" && (
          <button
            type="button"
            className="crm-doc-tracker__add"
            onClick={() => sendForSignature(documents[0])}
            disabled={saving}
          >
            <Share2 size={14} /> Send for signature
          </button>
        )}
      </div>

      {!documents.length && (
        <p className="crm-insights__muted">No documents tracked yet. Log when a quote is sent.</p>
      )}

      <ul className="crm-doc-tracker__list">
        {documents.map((doc) => (
          <li key={doc.id} className={`crm-doc-tracker__item crm-doc-tracker__item--${doc.status}`}>
            <div>
              <strong>{doc.title}</strong>
              <span className="crm-doc-tracker__status">{STATUS_LABELS[doc.status] || doc.status}</span>
              {doc.amount ? <span className="crm-insights__muted"> · ₹{doc.amount}</span> : null}
            </div>
            <div className="crm-doc-tracker__actions">
              {doc.status === "sent" && (
                <button type="button" onClick={() => updateStatus(doc.id, "viewed")} disabled={saving}>
                  <Eye size={14} /> Viewed
                </button>
              )}
              {(doc.status === "sent" || doc.status === "viewed") && (
                <button type="button" onClick={() => updateStatus(doc.id, "signed")} disabled={saving}>
                  <PenLine size={14} /> Signed
                </button>
              )}
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
};

export default CrmDocumentTracker;
